import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  ArrowLeft, User, GraduationCap, ClipboardCheck, BookOpenCheck, Wallet,
  Boxes, HeartPulse, Gavel, FileText, Users2, Plus, Trash2, ArrowUpRight,
  Pencil, Lock, Clock, Camera as CameraIcon, X as XIcon, Edit3
} from "lucide-react";
import { toast } from "sonner";
import StudentFinancePanel from "@/components/finance/StudentFinancePanel";
import { AssetImageUploader } from "@/components/AssetImageUploader";
import GuardianPicker from "@/components/GuardianPicker";

const PROGRAM_TINT = {
  CONV: "bg-[#1B2C5A] text-white border-[#1B2C5A]",
  CRECHE: "bg-pink-100 text-pink-800 border-pink-200",
  ISL: "bg-emerald-100 text-emerald-800 border-emerald-200",
  TAHFEEZ: "bg-amber-100 text-amber-800 border-amber-200",
};

const fmtDate = (s) => s ? new Date(s).toLocaleDateString() : "—";

const StudentProfile = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const effectiveCapabilities = user?.effective_capabilities || [];
  const userRoles = user?.roles || (user?.role ? [user.role] : []);
  const isSuperAdmin = userRoles.includes("super_admin");
  const hasEffectiveCapability = (capability) =>
    isSuperAdmin || effectiveCapabilities.includes(capability);
  const canEditStudent =
    hasEffectiveCapability("student.edit");
  const canManageEnrollment =
    hasEffectiveCapability("student.manage_enrollment");
  const canPromote =
    hasEffectiveCapability("promotion.execute");
  const canManageGuardians =
    hasEffectiveCapability("guardian.manage");
  const canReport = (user?.effective_capabilities || []).includes("report.create") || ["super_admin", "director", "teacher", "receptionist"].includes(user?.role);

  const [s, setS] = useState(null);
  const [enrollments, setEnrollments] = useState([]);
  const [programs, setPrograms] = useState([]);

  // Enroll dialog
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [enrollForm, setEnrollForm] = useState({ program_id: "", class_id: "", arm_id: "" });

  // Promote dialog
  const [promoteOpen, setPromoteOpen] = useState(false);
  const [promoteEnrollment, setPromoteEnrollment] = useState(null);
  const [promoteForm, setPromoteForm] = useState({ to_class_id: "", to_arm_id: "" });

  // Change Class (correction) dialog
  const [changeOpen, setChangeOpen] = useState(false);
  const [changeEnrollment, setChangeEnrollment] = useState(null);
  const [changeForm, setChangeForm] = useState({ class_id: "", arm_id: "" });

  // Edit profile dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  // Photo dialog
  const [photoOpen, setPhotoOpen] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);

  // Guardians / Siblings
  const [guardians, setGuardians] = useState([]);
  const [siblings, setSiblings] = useState([]);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkValue, setLinkValue] = useState({ mode: "new", id: null, full_name: "", relationship: "", phone: "", phone_alt: "", email: "" });

  const load = async () => {
    try {
      const [stRes, enrRes, progRes, gRes, sibRes] = await Promise.all([
        api.get(`/students/${id}`),
        api.get(`/students/${id}/enrollments`),
        api.get("/programs", { params: { active_only: true } }),
        api.get(`/students/${id}/guardians`),
        api.get(`/students/${id}/siblings`),
      ]);
      setS(stRes.data); setEnrollments(enrRes.data);
      setGuardians(gRes.data); setSiblings(sibRes.data);
      const enriched = await Promise.all(progRes.data.map(async (p) => {
        const cls = (await api.get("/classes", { params: { program_id: p.id } })).data;
        return { ...p, classes: cls };
      }));
      setPrograms(enriched);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!s) return <div className="text-[#475569]">Loading student profile...</div>;

  const enrollSelectedProgram = programs.find(p => p.id === enrollForm.program_id);
  const enrollClasses = enrollSelectedProgram?.classes || [];
  const enrollArms = enrollClasses.find(c => c.id === enrollForm.class_id)?.arms || [];
  const promoteProgram = programs.find(p => p.id === promoteEnrollment?.program_id);
  const promoteClasses = promoteProgram?.classes || [];
  const promoteArms = promoteClasses.find(c => c.id === promoteForm.to_class_id)?.arms || [];

  // Change-class dialog sources
  const changeProgram = programs.find(p => p.id === changeEnrollment?.program_id);
  const changeClasses = changeProgram?.classes || [];
  const changeArms = changeClasses.find(c => c.id === changeForm.class_id)?.arms || [];

  const availablePrograms = programs.filter(
    p => !enrollments.some(e => e.program_id === p.id && e.status === "active")
  );

  const enroll = async () => {
    if (!enrollForm.program_id || !enrollForm.class_id) return toast.error("Pick a program and class");
    try {
      await api.post(`/students/${id}/enrollments`, {
        program_id: enrollForm.program_id,
        class_id: enrollForm.class_id,
        arm_id: enrollForm.arm_id || undefined,
      });
      toast.success("Enrolled");
      setEnrollOpen(false); setEnrollForm({ program_id: "", class_id: "", arm_id: "" });
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const withdraw = async (enrollment_id) => {
    if (!window.confirm("Withdraw from this program? This removes the enrollment record.")) return;
    try {
      await api.delete(`/students/${id}/enrollments/${enrollment_id}`);
      toast.success("Withdrawn"); await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openPromote = (e) => { setPromoteEnrollment(e); setPromoteForm({ to_class_id: "", to_arm_id: "" }); setPromoteOpen(true); };
  const promote = async () => {
    if (!promoteForm.to_class_id) return toast.error("Pick a target class");
    try {
      await api.post(`/students/${id}/enrollments/${promoteEnrollment.id}/promote`, {
        to_class_id: promoteForm.to_class_id,
        to_arm_id: promoteForm.to_arm_id || undefined,
      });
      toast.success("Promoted");
      setPromoteOpen(false); setPromoteEnrollment(null);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openChange = (e) => {
    setChangeEnrollment(e);
    setChangeForm({ class_id: e.class_id || "", arm_id: e.arm_id || "" });
    setChangeOpen(true);
  };
  const saveChange = async () => {
    if (!changeForm.class_id) return toast.error("Pick a class");
    try {
      await api.patch(`/students/${id}/enrollments/${changeEnrollment.id}`, {
        class_id: changeForm.class_id,
        arm_id: changeForm.arm_id || null,
      });
      toast.success("Class corrected");
      setChangeOpen(false); setChangeEnrollment(null);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openEdit = () => {
    setEditForm({
      first_name: s.first_name || "", other_names: s.other_names || "", last_name: s.last_name || "",
      gender: s.gender || "male", date_of_birth: s.date_of_birth || "",
      phone: s.phone || "", blood_group: s.blood_group || "",
      address: s.address || "", admission_date: s.admission_date || "",
      status: s.status || "active",
      guardian_name: s.guardian_name || "", guardian_relationship: s.guardian_relationship || "",
      guardian_phone: s.guardian_phone || "", guardian_phone_alt: s.guardian_phone_alt || "",
      guardian_email: s.guardian_email || "",
      medical_notes: s.medical_notes || "",
    });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    if (!editForm.first_name?.trim() || !editForm.last_name?.trim()) {
      return toast.error("First name and Surname are required");
    }
    setSaving(true);
    try {
      // Strip empties so we don't blow away unset fields with "" (backend treats None as no-op)
      const payload = Object.fromEntries(Object.entries(editForm).filter(([_, v]) => v !== undefined && v !== null));
      await api.patch(`/students/${id}`, payload);
      toast.success("Profile updated");
      setEditOpen(false);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const uploadPhoto = async (files) => {
    if (!files || files.length === 0) return;
    const f = files[0];
    setPhotoBusy(true);
    try {
      await api.post(`/students/${id}/photo`, { kind: "main", name: f.name, type: f.type, data: f.data });
      toast.success("Photo updated");
      setPhotoOpen(false);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setPhotoBusy(false); }
  };

  const removePhoto = async () => {
    if (!window.confirm("Remove this photo?")) return;
    try {
      await api.delete(`/students/${id}/photo`);
      toast.success("Photo removed");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Guardian helpers
  const linkGuardian = async () => {
    let gid = linkValue.id;
    if (linkValue.mode === "new" || !gid) {
      if (!linkValue.full_name?.trim() && !linkValue.phone && !linkValue.email) return toast.error("Provide a guardian name, phone or email");
      try {
        const { data } = await api.post("/guardians", {
          full_name: linkValue.full_name?.trim() || "—",
          relationship: linkValue.relationship || undefined,
          phone: linkValue.phone || undefined, phone_alt: linkValue.phone_alt || undefined,
          email: linkValue.email || undefined,
        });
        gid = data.guardian.id;
      } catch (e) { return toast.error(formatApiError(e)); }
    }
    try {
      await api.post(`/students/${id}/guardians`, { guardian_id: gid, relationship: linkValue.relationship || undefined, is_primary: guardians.length === 0 });
      toast.success("Guardian linked");
      setLinkOpen(false);
      setLinkValue({ mode: "new", id: null, full_name: "", relationship: "", phone: "", phone_alt: "", email: "" });
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setPrimaryGuardian = async (link_id) => {
    try { await api.patch(`/students/${id}/guardians/${link_id}`, { is_primary: true }); toast.success("Primary set"); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const unlinkGuardian = async (link_id) => {
    if (!window.confirm("Unlink this guardian from this student? (Guardian record stays in the system.)")) return;
    try { await api.delete(`/students/${id}/guardians/${link_id}`); toast.success("Unlinked"); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="student-profile-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <Button variant="ghost" onClick={() => nav("/app/students")} className="text-[#1B2C5A]" data-testid="back-to-students">
          <ArrowLeft className="w-4 h-4 mr-1.5" /> All Students
        </Button>
        <div className="flex gap-2">
        {canReport && <Button variant="outline" onClick={() => nav(`/app/reports/new?person_type=student&person_id=${id}&name=${encodeURIComponent(s.full_name || "Student")}`)} data-testid="student-create-report">Create Report</Button>}
        <Button variant="outline" onClick={() => nav(`/app/id-cards?type=student&id=${id}`)} data-testid="student-id-card">ID Card</Button>
        {canEditStudent && (
          <Button onClick={openEdit} className="bg-[#9DCD3F] hover:bg-[#88BB35] text-[#1B2C5A] font-medium shadow-sm" data-testid="edit-profile-btn">
            <Pencil className="w-4 h-4 mr-1.5" /> Edit Profile
          </Button>
        )}
        </div>
      </div>

      {/* Hero */}
      <Card className="p-6 border-[#E2E8F0]">
        <div className="flex flex-col md:flex-row gap-6 items-start">
          <div className="relative shrink-0">
            <button
              type="button"
              onClick={() => canEditStudent && setPhotoOpen(true)}
              disabled={!canEditStudent}
              className="w-24 h-24 rounded-full overflow-hidden bg-[#1B2C5A] text-[#9DCD3F] grid place-items-center font-display text-2xl ring-4 ring-[#9DCD3F]/20 group relative cursor-pointer disabled:cursor-default"
              data-testid="student-photo-btn"
              aria-label={s.photo ? "Change photo" : "Add photo"}
            >
              {s.photo ? (
                <img src={s.photo} alt={s.full_name} className="w-full h-full object-cover" />
              ) : (
                <span>{(s.first_name?.[0] || s.full_name?.[0] || "?").toUpperCase()}{(s.last_name?.[0] || "").toUpperCase()}</span>
              )}
              {canEditStudent && (
                <span className="absolute inset-0 bg-black/55 text-white opacity-0 group-hover:opacity-100 transition flex items-center justify-center text-[10px] font-medium uppercase tracking-wider">
                  <CameraIcon className="w-4 h-4 mr-1" /> {s.photo ? "Change" : "Add"}
                </span>
              )}
            </button>
            {canEditStudent && !s.photo && (
              <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 text-[9px] uppercase tracking-wider text-[#9DCD3F] font-medium bg-[#1B2C5A] px-1.5 py-0.5 rounded-full">Add photo</span>
            )}
          </div>
          <div className="flex-1">
            <div className="overline text-[#9DCD3F] inline-flex items-center gap-1.5">
              <Lock className="w-3 h-3" /> Admission · {s.admission_no}
            </div>
            <h1 className="font-display text-3xl text-[#1B2C5A] mt-1 leading-tight">{s.full_name}</h1>
            <p className="text-sm text-[#475569] mt-1 capitalize">{s.gender} · DOB {fmtDate(s.date_of_birth)} · Admitted {fmtDate(s.admission_date)}</p>
            <div className="mt-3 flex flex-wrap gap-2" data-testid="program-pills">
              {enrollments.filter(e => e.status === "active").map(e => (
                <span key={e.id} className={"text-xs px-2.5 py-1 rounded-full border " + (PROGRAM_TINT[e.program_code] || "bg-slate-100 text-slate-700 border-slate-200")}>
                  {e.program_name} · {e.class_name}{e.arm_name ? ` ${e.arm_name}` : ""}
                </span>
              ))}
              {enrollments.filter(e => e.status === "active").length === 0 && (
                <span className="text-xs text-[#475569]">No active program enrollment yet</span>
              )}
            </div>
            {s.updated_at && (
              <div className="mt-3 text-xs text-[#475569] inline-flex items-center gap-1.5" data-testid="last-edited">
                <Clock className="w-3 h-3" /> Last edited by <strong className="text-[#1B2C5A]">{s.updated_by_name || "—"}</strong> on {new Date(s.updated_at).toLocaleString()}
              </div>
            )}
          </div>
        </div>
      </Card>

      <Tabs defaultValue={searchParams.get("tab") === "finance" ? "finance" : "bio"}>
        <TabsList className="bg-white border border-[#E2E8F0] flex-wrap h-auto">
          <TabsTrigger value="bio" data-testid="tab-bio"><User className="w-3.5 h-3.5 mr-1.5" /> Bio Data</TabsTrigger>
          <TabsTrigger value="guardians" data-testid="tab-guardians"><Users2 className="w-3.5 h-3.5 mr-1.5" /> Parents/Guardians</TabsTrigger>
          <TabsTrigger value="programs" data-testid="tab-programs"><GraduationCap className="w-3.5 h-3.5 mr-1.5" /> Programs ({enrollments.length})</TabsTrigger>
          <TabsTrigger value="attendance" data-testid="tab-attendance"><ClipboardCheck className="w-3.5 h-3.5 mr-1.5" /> Attendance</TabsTrigger>
          <TabsTrigger value="results" data-testid="tab-results"><BookOpenCheck className="w-3.5 h-3.5 mr-1.5" /> Results</TabsTrigger>
          <TabsTrigger value="finance" data-testid="tab-finance"><Wallet className="w-3.5 h-3.5 mr-1.5" /> Finance</TabsTrigger>
          <TabsTrigger value="inventory" data-testid="tab-inventory"><Boxes className="w-3.5 h-3.5 mr-1.5" /> Inventory</TabsTrigger>
          <TabsTrigger value="medical" data-testid="tab-medical"><HeartPulse className="w-3.5 h-3.5 mr-1.5" /> Medical</TabsTrigger>
          <TabsTrigger value="discipline" data-testid="tab-discipline"><Gavel className="w-3.5 h-3.5 mr-1.5" /> Discipline</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents"><FileText className="w-3.5 h-3.5 mr-1.5" /> Documents</TabsTrigger>
        </TabsList>

        {/* BIO */}
        <TabsContent value="bio" className="mt-4">
          <Card className="p-6 border-[#E2E8F0]">
            <h3 className="font-display text-lg text-[#1B2C5A] flex items-center gap-2 before:content-[''] before:w-1 before:h-5 before:bg-[#9DCD3F] before:rounded-full">Bio Data</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              <Detail label="First Name" value={s.first_name} />
              <Detail label="Other Names" value={s.other_names} />
              <Detail label="Last Name" value={s.last_name} />
              <Detail label="Gender" value={s.gender} />
              <Detail label="Date of Birth" value={fmtDate(s.date_of_birth)} />
              <Detail label="Blood Group" value={s.blood_group} />
              <Detail label="Phone" value={s.phone} />
              <Detail label="Status" value={s.status} />
              <Detail label="Admission Date" value={fmtDate(s.admission_date)} />
              <Detail label="Address" value={s.address} full />
            </div>
          </Card>
        </TabsContent>

        {/* PROGRAMS */}
        <TabsContent value="programs" className="mt-4">
          <Card className="p-6 border-[#E2E8F0]">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg text-[#1B2C5A] flex items-center gap-2 before:content-[''] before:w-1 before:h-5 before:bg-[#9DCD3F] before:rounded-full">Programs & Enrollments</h3>
              {canManageEnrollment && availablePrograms.length > 0 && (
                <Button onClick={() => setEnrollOpen(true)} className="bg-[#9DCD3F] hover:bg-[#88BB35] text-[#1B2C5A]" data-testid="enroll-btn"><Plus className="w-4 h-4 mr-1.5" /> Add Program</Button>
              )}
            </div>
            {enrollments.length === 0 ? (
              <p className="text-sm text-[#475569]">Not enrolled in any program yet.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {enrollments.map(e => (
                  <div key={e.id} className={"rounded-xl border p-4 " + (e.status === "active" ? "border-[#E2E8F0] bg-white" : "border-dashed border-slate-300 bg-slate-50 opacity-75")} data-testid={`enrollment-${e.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={"text-xs px-2 py-0.5 rounded-full border " + (PROGRAM_TINT[e.program_code] || "")}>{e.program_name}</span>
                          {e.status === "active"
                            ? <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">Active</span>
                            : <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200 capitalize">{e.status}</span>}
                        </div>
                        <div className="mt-2 text-sm text-[#1B2C5A] font-medium">
                          {e.class_name}{e.arm_name ? ` · ${e.arm_name}` : ""}
                        </div>
                        <div className="text-xs text-[#475569] mt-1">
                          {e.session_name || "Session —"} · Enrolled {fmtDate(e.enrollment_date)}
                        </div>
                      </div>
                      {(canManageEnrollment || canPromote) && e.status === "active" && (
                        <div className="flex flex-col gap-1">
                          {canManageEnrollment && (
                            <Button size="sm" variant="ghost" onClick={() => openChange(e)} className="text-[#1B2C5A]" data-testid={`change-class-${e.id}`} title="Correct class/arm (fix mistake)"><Edit3 className="w-4 h-4" /></Button>
                          )}
                          {canPromote && (
                            <Button size="sm" variant="ghost" onClick={() => openPromote(e)} className="text-[#1B2C5A]" data-testid={`promote-${e.id}`} title="Promote to next class"><ArrowUpRight className="w-4 h-4" /></Button>
                          )}
                          {canManageEnrollment && (
                            <Button size="sm" variant="ghost" onClick={() => withdraw(e.id)} className="text-red-700" data-testid={`withdraw-${e.id}`} title="Withdraw"><Trash2 className="w-4 h-4" /></Button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>

        {/* GUARDIANS / FAMILY */}
        <TabsContent value="guardians" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Linked guardians */}
            <Card className="lg:col-span-2 p-6 border-[#E2E8F0]">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display text-lg text-[#1B2C5A] flex items-center gap-2 before:content-[''] before:w-1 before:h-5 before:bg-[#9DCD3F] before:rounded-full">
                  Parents / Guardians ({guardians.length})
                </h3>
                {canManageGuardians && (
                  <Button onClick={() => setLinkOpen(true)} className="bg-[#9DCD3F] hover:bg-[#88BB35] text-[#1B2C5A]" data-testid="link-guardian-btn">
                    <Plus className="w-4 h-4 mr-1.5" /> Link Guardian
                  </Button>
                )}
              </div>
              {guardians.length === 0 ? (
                <p className="text-sm text-[#475569]">No guardian linked yet. Click <strong>Link Guardian</strong> to add one (existing or new).</p>
              ) : (
                <div className="grid gap-3">
                  {guardians.map(g => (
                    <div key={g.link_id} className={"rounded-lg border p-3 flex gap-3 items-start " + (g.is_primary ? "border-[#9DCD3F] bg-[#9DCD3F]/8" : "border-[#E2E8F0] bg-white")} data-testid={`guardian-${g.link_id}`}>
                      <div className="w-10 h-10 rounded-full bg-[#1B2C5A] text-[#9DCD3F] grid place-items-center font-display shrink-0">
                        {(g.full_name?.[0] || "G").toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-medium text-[#1B2C5A] truncate">{g.full_name}</span>
                          {g.is_primary && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#1B2C5A] text-[#9DCD3F] font-medium">Primary</span>}
                          {g.relationship && <span className="text-xs text-[#475569]">· {g.relationship}</span>}
                        </div>
                        <div className="text-xs text-[#475569] mt-1 flex flex-wrap gap-3">
                          {g.phone && <span>📞 {g.phone}</span>}
                          {g.phone_alt && <span>· {g.phone_alt}</span>}
                          {g.email && <span>· {g.email}</span>}
                        </div>
                      </div>
                      {canManageGuardians && (
                        <div className="flex flex-col gap-1">
                          {!g.is_primary && (
                            <Button size="sm" variant="ghost" onClick={() => setPrimaryGuardian(g.link_id)} className="text-[#1B2C5A] text-xs" data-testid={`set-primary-${g.link_id}`} title="Set as primary">
                              ★ Primary
                            </Button>
                          )}
                          <Button size="sm" variant="ghost" onClick={() => unlinkGuardian(g.link_id)} className="text-red-700" data-testid={`unlink-${g.link_id}`} title="Unlink (guardian record stays)">
                            <XIcon className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Card>

            {/* Siblings sidebar */}
            <Card className="p-6 border-[#E2E8F0]" data-testid="siblings-card">
              <h3 className="font-display text-lg text-[#1B2C5A] flex items-center gap-2 before:content-[''] before:w-1 before:h-5 before:bg-[#9DCD3F] before:rounded-full">
                <Users2 className="w-4 h-4 text-[#9DCD3F] ml-1" /> Siblings ({siblings.length})
              </h3>
              <p className="text-xs text-[#475569] mt-1 mb-3">Other students sharing a parent/guardian.</p>
              {siblings.length === 0 ? (
                <p className="text-sm text-[#475569]">No siblings on record.</p>
              ) : (
                <div className="space-y-2">
                  {siblings.map(sib => (
                    <button key={sib.id} onClick={() => nav(`/app/students/${sib.id}`)} className="w-full text-left flex items-center gap-3 p-2 rounded-lg hover:bg-[#9DCD3F]/10 transition border border-transparent hover:border-[#9DCD3F]/30" data-testid={`sibling-${sib.id}`}>
                      {sib.photo ? (
                        <img src={sib.photo} alt={sib.full_name} className="w-9 h-9 rounded-full object-cover" loading="lazy" />
                      ) : (
                        <span className="w-9 h-9 rounded-full bg-[#1B2C5A] text-[#9DCD3F] grid place-items-center text-xs font-medium shrink-0">
                          {(sib.full_name?.[0] || "?").toUpperCase()}
                        </span>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-[#1B2C5A] truncate">{sib.full_name}</div>
                        <div className="text-xs text-[#475569] font-mono truncate">{sib.admission_no}</div>
                      </div>
                      <ArrowUpRight className="w-3.5 h-3.5 text-[#9DCD3F]" />
                    </button>
                  ))}
                </div>
              )}
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="finance" className="mt-4">
          <StudentFinancePanel student={s} enrollments={enrollments} />
        </TabsContent>

        {/* Placeholders for upcoming tabs */}
        {[
          ["attendance", "Attendance (program-specific)"],
          ["results", "Academic Results (per program)"],
          ["inventory", "Inventory & Issued Items"],
          ["medical", "Medical Records"],
          ["discipline", "Discipline Records"],
          ["documents", "Documents"],
        ].map(([key, label]) => (
          <TabsContent key={key} value={key} className="mt-4">
            <Card className="p-6 border-[#E2E8F0]">
              <h3 className="font-display text-lg text-[#1B2C5A]">{label}</h3>
              <p className="text-sm text-[#475569] mt-3">Coming next — this tab will surface {label.toLowerCase()} information per active program enrollment.</p>
            </Card>
          </TabsContent>
        ))}
      </Tabs>

      {/* Enroll Dialog */}
      <Dialog open={enrollOpen} onOpenChange={setEnrollOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display text-[#1B2C5A]">Enroll in a Program</DialogTitle></DialogHeader>
          <div className="grid gap-4 mt-2">
            <Fld label="Program">
              <Select value={enrollForm.program_id} onValueChange={(v) => setEnrollForm({ program_id: v, class_id: "", arm_id: "" })}>
                <SelectTrigger data-testid="enroll-program"><SelectValue placeholder="Pick a program" /></SelectTrigger>
                <SelectContent>
                  {availablePrograms.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Fld>
            <Fld label="Class">
              <Select value={enrollForm.class_id} onValueChange={(v) => setEnrollForm({ ...enrollForm, class_id: v, arm_id: "" })} disabled={!enrollClasses.length}>
                <SelectTrigger data-testid="enroll-class"><SelectValue placeholder={enrollClasses.length ? "Pick a class" : "Pick program first"} /></SelectTrigger>
                <SelectContent>
                  {enrollClasses.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Fld>
            <Fld label="Arm (optional)">
              <Select value={enrollForm.arm_id} onValueChange={(v) => setEnrollForm({ ...enrollForm, arm_id: v })} disabled={!enrollArms.length}>
                <SelectTrigger data-testid="enroll-arm"><SelectValue placeholder={enrollArms.length ? "Pick arm" : "—"} /></SelectTrigger>
                <SelectContent>
                  {enrollArms.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Fld>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEnrollOpen(false)}>Cancel</Button>
            <Button onClick={enroll} className="bg-[#1B2C5A] hover:bg-[#14224A] text-white" data-testid="enroll-submit">Enroll</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Promote Dialog */}
      <Dialog open={promoteOpen} onOpenChange={setPromoteOpen}>        <DialogContent>
          <DialogHeader><DialogTitle className="font-display text-[#1B2C5A]">Promote within {promoteEnrollment?.program_name}</DialogTitle></DialogHeader>
          <p className="text-sm text-[#475569]">Promote from <strong>{promoteEnrollment?.class_name}{promoteEnrollment?.arm_name ? ` ${promoteEnrollment.arm_name}` : ""}</strong> to a new class.</p>
          <div className="grid gap-4 mt-2">
            <Fld label="To Class">
              <Select value={promoteForm.to_class_id} onValueChange={(v) => setPromoteForm({ to_class_id: v, to_arm_id: "" })}>
                <SelectTrigger data-testid="promote-class"><SelectValue placeholder="Pick target class" /></SelectTrigger>
                <SelectContent>
                  {promoteClasses.filter(c => c.id !== promoteEnrollment?.class_id).map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Fld>
            <Fld label="To Arm (optional)">
              <Select value={promoteForm.to_arm_id} onValueChange={(v) => setPromoteForm({ ...promoteForm, to_arm_id: v })} disabled={!promoteArms.length}>
                <SelectTrigger data-testid="promote-arm"><SelectValue placeholder={promoteArms.length ? "Pick arm" : "—"} /></SelectTrigger>
                <SelectContent>
                  {promoteArms.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Fld>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPromoteOpen(false)}>Cancel</Button>
            <Button onClick={promote} className="bg-[#9DCD3F] hover:bg-[#88BB35] text-[#1B2C5A]" data-testid="promote-submit">Promote</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Class Dialog (correction, no promotion history) */}
      <Dialog open={changeOpen} onOpenChange={setChangeOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display text-[#1B2C5A] flex items-center gap-2">
              <Edit3 className="w-4 h-4 text-[#9DCD3F]" /> Correct Class — {changeEnrollment?.program_name}
            </DialogTitle>
          </DialogHeader>
          <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900">
            <strong>Use this to fix a wrong class assignment.</strong> The current enrollment will be updated in place (no promotion history). For end-of-term advancement, use the green Promote arrow ↗ instead.
          </div>
          <div className="grid gap-4 mt-2">
            <Fld label="Class">
              <Select value={changeForm.class_id} onValueChange={(v) => setChangeForm({ class_id: v, arm_id: "" })}>
                <SelectTrigger data-testid="change-class-select"><SelectValue placeholder="Pick the correct class" /></SelectTrigger>
                <SelectContent>
                  {changeClasses.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Fld>
            <Fld label="Arm (optional)">
              <Select value={changeForm.arm_id} onValueChange={(v) => setChangeForm({ ...changeForm, arm_id: v })} disabled={!changeArms.length}>
                <SelectTrigger data-testid="change-arm-select"><SelectValue placeholder={changeArms.length ? "Pick arm" : "—"} /></SelectTrigger>
                <SelectContent>
                  {changeArms.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Fld>
            <div className="text-xs text-[#475569] inline-flex items-center gap-1.5">
              <Clock className="w-3 h-3" /> Current: <strong className="text-[#1B2C5A]">{changeEnrollment?.class_name}{changeEnrollment?.arm_name ? ` · ${changeEnrollment.arm_name}` : ""}</strong>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setChangeOpen(false)}>Cancel</Button>
            <Button onClick={saveChange} className="bg-[#1B2C5A] hover:bg-[#14224A] text-white" data-testid="change-submit">Save Correction</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Photo Upload Dialog */}
      <Dialog open={photoOpen} onOpenChange={setPhotoOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display text-[#1B2C5A] flex items-center gap-2">
              <CameraIcon className="w-4 h-4 text-[#9DCD3F]" /> {s.photo ? "Change Photo" : "Add Photo"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {s.photo && (
              <div className="flex items-center gap-4 p-3 rounded-lg bg-[#FDFBF7] border border-[#E2E8F0]">
                <img src={s.photo} alt="Current" className="w-20 h-20 rounded-full object-cover ring-2 ring-[#9DCD3F]/30" />
                <div className="flex-1 text-sm">
                  <div className="text-[#1B2C5A] font-medium">Current photo</div>
                  <div className="text-xs text-[#475569]">Uploaded by {s.photo_meta?.uploaded_by || "—"}</div>
                </div>
                <Button size="sm" variant="ghost" onClick={removePhoto} className="text-red-700" data-testid="student-photo-remove">
                  <XIcon className="w-3.5 h-3.5 mr-1" /> Remove
                </Button>
              </div>
            )}
            <AssetImageUploader
              onPick={uploadPhoto}
              label={photoBusy ? "Uploading…" : "Upload student photo (drag-and-drop, choose file, or camera)"}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPhotoOpen(false)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Profile Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-[#1B2C5A] flex items-center gap-2">
              <Pencil className="w-4 h-4 text-[#9DCD3F]" /> Edit Student Profile
            </DialogTitle>
          </DialogHeader>

          {/* Locked admission number */}
          <div className="rounded-lg bg-slate-100 border border-slate-200 px-3 py-2 flex items-center gap-2 text-sm" data-testid="admission-locked">
            <Lock className="w-3.5 h-3.5 text-slate-500" />
            <span className="text-[#475569]">Admission No</span>
            <span className="font-mono font-semibold text-[#1B2C5A]">{s.admission_no}</span>
            <span className="ml-auto text-xs text-[#475569] italic">read-only · cannot be changed</span>
          </div>

          {/* Bio */}
          <div className="mt-3">
            <h4 className="text-xs uppercase tracking-wider text-[#9DCD3F] font-medium mb-2">Bio Data</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <Fld label="First Name"><Input value={editForm.first_name || ""} onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })} data-testid="edit-first-name" /></Fld>
              <Fld label="Other Names"><Input value={editForm.other_names || ""} onChange={(e) => setEditForm({ ...editForm, other_names: e.target.value })} data-testid="edit-other-names" /></Fld>
              <Fld label="Surname"><Input value={editForm.last_name || ""} onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })} data-testid="edit-last-name" /></Fld>
              <Fld label="Gender">
                <Select value={editForm.gender || "male"} onValueChange={(v) => setEditForm({ ...editForm, gender: v })}>
                  <SelectTrigger data-testid="edit-gender"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                  </SelectContent>
                </Select>
              </Fld>
              <Fld label="Date of Birth"><Input type="date" value={editForm.date_of_birth || ""} onChange={(e) => setEditForm({ ...editForm, date_of_birth: e.target.value })} data-testid="edit-dob" /></Fld>
              <Fld label="Admission Date"><Input type="date" value={editForm.admission_date || ""} onChange={(e) => setEditForm({ ...editForm, admission_date: e.target.value })} /></Fld>
              <Fld label="Status">
                <Select value={editForm.status || "active"} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
                  <SelectTrigger data-testid="edit-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="graduated">Graduated</SelectItem>
                    <SelectItem value="withdrawn">Withdrawn</SelectItem>
                  </SelectContent>
                </Select>
              </Fld>
              <Fld label="Phone"><Input value={editForm.phone || ""} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} /></Fld>
              <Fld label="Blood Group"><Input value={editForm.blood_group || ""} onChange={(e) => setEditForm({ ...editForm, blood_group: e.target.value })} placeholder="O+" /></Fld>
            </div>
          </div>

          <div className="mt-4">
            <h4 className="text-xs uppercase tracking-wider text-[#9DCD3F] font-medium mb-2">Address & Medical</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2"><Fld label="Address"><Input value={editForm.address || ""} onChange={(e) => setEditForm({ ...editForm, address: e.target.value })} data-testid="edit-address" /></Fld></div>
              <div className="md:col-span-2"><Fld label="Medical Notes"><Textarea rows={2} value={editForm.medical_notes || ""} onChange={(e) => setEditForm({ ...editForm, medical_notes: e.target.value })} data-testid="edit-medical" /></Fld></div>
            </div>
          </div>

          <div className="mt-4">
            <h4 className="text-xs uppercase tracking-wider text-[#9DCD3F] font-medium mb-2">Guardian</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <Fld label="Name"><Input value={editForm.guardian_name || ""} onChange={(e) => setEditForm({ ...editForm, guardian_name: e.target.value })} data-testid="edit-guardian-name" /></Fld>
              <Fld label="Relationship"><Input value={editForm.guardian_relationship || ""} onChange={(e) => setEditForm({ ...editForm, guardian_relationship: e.target.value })} placeholder="Father / Mother / …" /></Fld>
              <Fld label="Email"><Input type="email" value={editForm.guardian_email || ""} onChange={(e) => setEditForm({ ...editForm, guardian_email: e.target.value })} /></Fld>
              <Fld label="Phone (Primary)"><Input value={editForm.guardian_phone || ""} onChange={(e) => setEditForm({ ...editForm, guardian_phone: e.target.value })} data-testid="edit-guardian-phone" /></Fld>
              <Fld label="Phone (Alt)"><Input value={editForm.guardian_phone_alt || ""} onChange={(e) => setEditForm({ ...editForm, guardian_phone_alt: e.target.value })} /></Fld>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={saveEdit} disabled={saving} className="bg-[#1B2C5A] hover:bg-[#14224A] text-white" data-testid="edit-save-btn">
              {saving ? "Saving…" : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Link Guardian Dialog */}
      <Dialog open={linkOpen} onOpenChange={setLinkOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display text-[#1B2C5A] flex items-center gap-2">
              <Users2 className="w-4 h-4 text-[#9DCD3F]" /> Link Guardian to {s.first_name || s.full_name}
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-[#475569] -mt-2">Search an existing guardian (recommended — keeps families linked) or enter a brand-new one.</p>
          <div className="mt-2">
            <GuardianPicker value={linkValue} onChange={setLinkValue} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkOpen(false)}>Cancel</Button>
            <Button onClick={linkGuardian} className="bg-[#1B2C5A] hover:bg-[#14224A] text-white" data-testid="link-guardian-submit">
              Link Guardian
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Detail = ({ label, value, full }) => (
  <div className={"p-3 rounded-lg bg-[#FDFBF7] border border-[#E2E8F0] " + (full ? "md:col-span-3" : "")}>
    <div className="overline text-[#9DCD3F]">{label}</div>
    <div className="text-sm text-[#1B2C5A] mt-1 break-words capitalize">{value || "—"}</div>
  </div>
);

const Fld = ({ label, children }) => (
  <div className="space-y-1.5"><Label className="text-[#1B2C5A] text-sm">{label}</Label>{children}</div>
);

export default StudentProfile;
